import React, { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const Lightbox = ({ artworks, index, onClose, onIndexChange }) => {
    const open = index !== null && index >= 0;
    const current = open ? artworks[index] : null;

    const next = useCallback(() => {
        if (!open) return;
        onIndexChange((index + 1) % artworks.length);
    }, [open, index, artworks.length, onIndexChange]);

    const prev = useCallback(() => {
        if (!open) return;
        onIndexChange((index - 1 + artworks.length) % artworks.length);
    }, [open, index, artworks.length, onIndexChange]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open, next, prev, onClose]);

    return (
        <AnimatePresence>
            {open && current && (
                <motion.div
                    data-testid="lightbox"
                    className="fixed inset-0 z-[100] bg-aplum flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <button
                        data-testid="lightbox-close"
                        onClick={onClose}
                        className="absolute top-6 right-6 md:top-10 md:right-10 text-aplum hover:text-aplum-accent transition-colors p-2 z-10"
                        aria-label="Close"
                    >
                        <X size={28} strokeWidth={1.2} />
                    </button>

                    <button
                        data-testid="lightbox-prev"
                        onClick={prev}
                        className="absolute left-3 md:left-10 top-1/2 -translate-y-1/2 text-aplum hover:text-aplum-accent transition-colors p-2 z-10"
                        aria-label="Previous"
                    >
                        <ChevronLeft size={36} strokeWidth={1.2} />
                    </button>

                    <button
                        data-testid="lightbox-next"
                        onClick={next}
                        className="absolute right-3 md:right-10 top-1/2 -translate-y-1/2 text-aplum hover:text-aplum-accent transition-colors p-2 z-10"
                        aria-label="Next"
                    >
                        <ChevronRight size={36} strokeWidth={1.2} />
                    </button>

                    <motion.div
                        key={current.id}
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-[90vw] max-h-[85vh] flex flex-col items-center gap-6 px-4"
                    >
                        <img
                            src={current.image}
                            alt={current.title}
                            className="max-h-[70vh] max-w-full object-contain"
                        />
                        <div className="text-center max-w-2xl">
                            <h3 className="font-serif-display text-2xl md:text-3xl text-aplum tracking-tight">
                                {current.title}
                            </h3>
                            <p className="font-sans-display text-xs uppercase tracking-widest-x text-aplum-dim mt-2">
                                {current.medium} · {current.year}
                            </p>
                            <p className="font-sans-display text-sm text-aplum-dim mt-4 leading-relaxed hidden md:block">
                                {current.description}
                            </p>
                        </div>
                        <span className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-dim">
                            {String(index + 1).padStart(2, "0")} / {String(artworks.length).padStart(2, "0")}
                        </span>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Lightbox;
